import { useEffect, useMemo, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"

import Label from "components/forms/label"
import SelectSearchable from "components/forms/select-searchable"

import useProductModelRepositories from "repositories/product-model"
import useProductUnitRepositories from "repositories/product-unit"

const formatNumber = (n) => Number(n || 0).toLocaleString("id-ID")

export default function ByModelModal({ freezeId, checkId, onClose, onSuccess }) {
  const [modelSearch, setModelSearch] = useState("")
  const [debouncedModelSearch, setDebouncedModelSearch] = useState("")
  const [selectedModelId, setSelectedModelId] = useState("")
  const [selectedModelRow, setSelectedModelRow] = useState(null)
  const [unitQty, setUnitQty] = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedModelSearch(modelSearch.trim()), 350)
    return () => clearTimeout(handle)
  }, [modelSearch])

  const { data: modelDataRaw, isLoading: isLoadingModels } = useProductModelRepositories({
    paginate: true,
    direction:"asc",
    search: debouncedModelSearch || undefined,
  })

  const modelOptions = useMemo(() => {
    if (Array.isArray(modelDataRaw)) return modelDataRaw
    if (Array.isArray(modelDataRaw?.data)) return modelDataRaw.data
    return []
  }, [modelDataRaw])

  const { data: unitDataRaw, isLoading: isLoadingUnits } = useProductUnitRepositories({
    paginate: false,
    product_model_id: selectedModelId || undefined,
  })

  const units = useMemo(() => {
    if (!selectedModelId) return []
    if (Array.isArray(unitDataRaw)) return unitDataRaw
    if (Array.isArray(unitDataRaw?.data)) return unitDataRaw.data
    return []
  }, [unitDataRaw, selectedModelId])

  useEffect(() => {
    // Reset qty inputs whenever model changes
    setUnitQty({})
  }, [selectedModelId])

  const totalQty = useMemo(
    () => Object.values(unitQty).reduce((acc, v) => acc + (Number(v) || 0), 0),
    [unitQty]
  )
  const filledCount = useMemo(
    () => Object.values(unitQty).filter((v) => v !== "" && v !== null && v !== undefined && Number(v) > 0).length,
    [unitQty]
  )

  const setQty = (id, val) => setUnitQty((prev) => ({ ...prev, [id]: val }))

  const fillAll = (val) => {
    const next = {}
    for (const u of units) next[u.id] = String(val)
    setUnitQty(next)
  }

  const submit = async () => {
    const items = Object.entries(unitQty)
      .map(([product_unit_id, q]) => ({ product_unit_id, quantity: Number(q) }))
      .filter((it) => it.product_unit_id && Number.isFinite(it.quantity) && it.quantity > 0)

    if (items.length === 0) {
      toast.error("Belum ada qty yang diisi. Isi minimal 1 unit.")
      return
    }

    setSubmitting(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${freezeId}/checks/${checkId}/items/bulk`,
        { items }
      )
      toast.success(res.data?.message || `${items.length} item ditambahkan`)
      onSuccess?.()
      onClose?.()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal menambahkan item secara bulk")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[90vh] bg-white sm:rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">Tambah per Model</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Pilih model lalu input qty fisik per unit. Qty 0/kosong di-skip.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-900 rounded-lg hover:bg-neutral-100"
          >
            ✕
          </button>
        </div>

        <div className="p-5 border-b border-neutral-200 bg-neutral-50">
          <Label value="Produk Model *" />
          <SelectSearchable
            selection={modelOptions}
            isLoading={isLoadingModels}
            value={selectedModelId}
            onChange={(value, row) => {
              setSelectedModelId(String(value || ""))
              setSelectedModelRow(row || null)
            }}
            keyValue={(v) => v.id}
            title={(v) => v.long_name || v.name}
            description={(v) => [v.slug, v.product_type?.name].filter(Boolean).join(" · ")}
            placeholder="Cari produk model..."
            onSearchChange={(q) => setModelSearch(String(q || ""))}
            serverSearch
            clearOnClose={false}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {!selectedModelId ? (
            <div className="p-10 text-sm text-center text-neutral-500">
              Pilih produk model dulu untuk lihat daftar unit-nya.
            </div>
          ) : isLoadingUnits ? (
            <div className="p-10 text-sm text-center text-neutral-500">Memuat unit...</div>
          ) : units.length === 0 ? (
            <div className="p-10 text-sm text-center text-neutral-500">
              Model ini tidak memiliki produk unit.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-neutral-200 bg-white flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-neutral-700">
                  <strong>{units.length}</strong> unit di model{" "}
                  <strong>{selectedModelRow?.long_name || selectedModelRow?.name}</strong>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-neutral-500">Isi cepat:</span>
                  {[0, 1, 5, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => fillAll(n)}
                      className="px-2.5 py-1 text-xs font-medium text-neutral-700 border rounded-lg border-neutral-300 bg-white hover:bg-neutral-100"
                    >
                      Semua {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setUnitQty({})}
                    className="px-2.5 py-1 text-xs font-medium text-rose-700 border rounded-lg border-rose-200 bg-rose-50 hover:bg-rose-100"
                  >
                    Hapus
                  </button>
                </div>
              </div>

              <ul className="divide-y divide-neutral-100">
                {units.map((u, idx) => (
                  <li key={u.id} className="px-5 py-3 flex items-center gap-3">
                    <span className="text-xs text-neutral-400 w-6 text-right">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-neutral-900 truncate">
                        {u.long_name || u.name}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {u.id}
                        {u.sku && <span className="ml-1">· {u.sku}</span>}
                      </div>
                    </div>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      value={unitQty[u.id] ?? ""}
                      onChange={(e) => setQty(u.id, e.target.value)}
                      placeholder="0"
                      className="w-24 px-3 py-2 text-right text-base font-medium border rounded-xl border-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-900"
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-neutral-200 bg-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-neutral-700">
            <strong>{filledCount}</strong> unit terisi · total{" "}
            <strong>{formatNumber(totalQty)}</strong> qty
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium text-neutral-700 border rounded-lg border-neutral-300 bg-white"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || filledCount === 0}
              className="flex-1 sm:flex-initial px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800 disabled:opacity-50"
            >
              {submitting ? "Memproses..." : `Tambah ${filledCount > 0 ? `${filledCount} item` : "item"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
